import React, { useEffect, useRef, useState } from "react";
import * as THREE from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import { EffectComposer } from "three/examples/jsm/postprocessing/EffectComposer.js";
import { RenderPass } from "three/examples/jsm/postprocessing/RenderPass.js";
import { UnrealBloomPass } from "three/examples/jsm/postprocessing/UnrealBloomPass.js";
import {
  createFireflyGroup,
  updateFireflies,
  screenToFireflySpace,
} from "./fireflies";
import { setupLogoAndText } from "./logoAndText";

import { FaGithub, FaLinkedin } from "react-icons/fa";
import { FaEnvelope } from "react-icons/fa";

function App() {
  const mountRef = useRef(null);
  const cameraRef = useRef(null);

  // "Home" orientation
  const originalXRef = useRef(0.3); // rotation.x at Home
  const originalYRef = useRef(0.3); // rotation.y at Home
  const originalZRef = useRef(70); // position.z at Home

  // "About" orientation
  const aboutXRef = useRef(0.05);
  const aboutZRef = useRef(-200);

  // Mouse + time
  const mousePosRef = useRef({ x: 0, y: 0 });
  const globalTimeRef = useRef(0);

  // Material refs
  const glowMaterialRef = useRef(null);
  const textMaterialRef = useRef(null);
  const subTextMeshRef = useRef(null);

  // Nav text
  const aboutMeshRef = useRef(null);
  const aboutHitboxRef = useRef(null);
  const contactMeshRef = useRef(null);
  const contactHitboxRef = useRef(null);
  const homeMeshRef = useRef(null);
  const homeHitboxRef = useRef(null);

  // Example about text
  const aboutExampleMeshRef = useRef(null);

  // Hover states
  const [aboutHovered, setAboutHovered] = useState(false);
  const [contactHovered, setContactHovered] = useState(false);
  const [homeHovered, setHomeHovered] = useState(false);
  const aboutHoveredRef = useRef(false);
  const contactHoveredRef = useRef(false);
  const homeHoveredRef = useRef(false);

  // Current section
  const [currentSection, setCurrentSection] = useState("home");
  const currentSectionRef = useRef(currentSection);
  useEffect(() => {
    currentSectionRef.current = currentSection;
  }, [currentSection]);

  // A small state to track if the overlay is fully visible
  const [aboutOverlayVisible, setAboutOverlayVisible] = useState(false);
  const [contactOverlayVisible, setContactOverlayVisible] = useState(false);

  // Logo-plane refs
  const outlineMeshRef = useRef(null);
  const logoMeshRef = useRef(null);

  // Fade-out flag
  const fadeOutStartedRef = useRef(false);

  // Fireflies
  const fireflyGroupsRef = useRef([]);

  // Two mixers: one for portal, one for dragon
  let mixerPortal = null;
  let mixerDragon = null;

  // Reference for the loaded dragon so we can rotate it:
  const dragonRef = useRef(null);

  // --- NEW: disable clicks for 3 seconds after each pointerDown
  const clickDisabledRef = useRef(false);

  // Keep hover states in sync
  useEffect(() => {
    aboutHoveredRef.current = aboutHovered;
  }, [aboutHovered]);
  useEffect(() => {
    contactHoveredRef.current = contactHovered;
  }, [contactHovered]);
  useEffect(() => {
    homeHoveredRef.current = homeHovered;
  }, [homeHovered]);

  // Whenever currentSection changes, if it's "about", we fade in
  // If not, we simply hide the overlay (no fade out).
  useEffect(() => {
    if (currentSection === "about") {
      // We delay setting the overlay to visible so that the <div> mounts first.
      // Then the CSS transition can animate from opacity: 0 -> 1.
      setTimeout(() => setAboutOverlayVisible(true), 0);
    } else {
      setAboutOverlayVisible(false);
    }
  }, [currentSection]);

  // ----------------------------------------------------
  // animateCameraAll with 12 arguments + duration
  // ----------------------------------------------------
  function animateCameraAll(
    fromPosX,
    toPosX,
    fromPosY,
    toPosY,
    fromPosZ,
    toPosZ,
    fromRotX,
    toRotX,
    fromRotY,
    toRotY,
    fromRotZ,
    toRotZ,
    duration
  ) {
    const startTime = performance.now();

    function animate() {
      const elapsed = performance.now() - startTime;
      const t = Math.min(elapsed / duration, 1);

      if (cameraRef.current) {
        // Position
        cameraRef.current.position.x = THREE.MathUtils.lerp(
          fromPosX,
          toPosX,
          t
        );
        cameraRef.current.position.y = THREE.MathUtils.lerp(
          fromPosY,
          toPosY,
          t
        );
        cameraRef.current.position.z = THREE.MathUtils.lerp(
          fromPosZ,
          toPosZ,
          t
        );

        // Rotation
        cameraRef.current.rotation.x = THREE.MathUtils.lerp(
          fromRotX,
          toRotX,
          t
        );
        cameraRef.current.rotation.y = THREE.MathUtils.lerp(
          fromRotY,
          toRotY,
          t
        );
        cameraRef.current.rotation.z = THREE.MathUtils.lerp(
          fromRotZ,
          toRotZ,
          t
        );
      }
      if (t < 1) requestAnimationFrame(animate);
    }

    requestAnimationFrame(animate);
  }

  function fadeInAboutExampleText() {
    const mesh = aboutExampleMeshRef.current;
    if (!mesh) return;

    setTimeout(() => {
      mesh.visible = true;
      const fadeDuration = 1000;
      const startTime = performance.now();
      const startOpacity = 0;
      const endOpacity = 0.385;

      function fade() {
        const elapsed = performance.now() - startTime;
        const t = Math.min(elapsed / fadeDuration, 1);
        mesh.material.opacity = THREE.MathUtils.lerp(
          startOpacity,
          endOpacity,
          t
        );
        if (t < 1) requestAnimationFrame(fade);
      }
      requestAnimationFrame(fade);
    }, 1000);
  }
  function fadeInContactExampleText() {
    const mesh = aboutExampleMeshRef.current;
    if (!mesh) return;

    setTimeout(() => {
      mesh.visible = true;
      const fadeDuration = 1000;
      const startTime = performance.now();
      const startOpacity = 0;
      const endOpacity = 0.385;

      function fade() {
        const elapsed = performance.now() - startTime;
        const t = Math.min(elapsed / fadeDuration, 1);
        mesh.material.opacity = THREE.MathUtils.lerp(
          startOpacity,
          endOpacity,
          t
        );
        if (t < 1) requestAnimationFrame(fade);
      }
      requestAnimationFrame(fade);
    }, 1000);
  }
  // ----------------------------------------------------
  // Fade in the HOME text after a 1-second delay
  // ----------------------------------------------------
  function fadeInHomeText() {
    const mesh = homeMeshRef.current;
    if (!mesh) return;

    // Wait 1 second before starting the fade
    setTimeout(() => {
      mesh.visible = true; // Ensure it’s rendered
      mesh.material.opacity = 0; // Start fully transparent

      const fadeDuration = 3000;
      const startTime = performance.now();
      const startOpacity = 0;
      const endOpacity = 0.385;

      function fade() {
        const elapsed = performance.now() - startTime;
        const t = Math.min(elapsed / fadeDuration, 1);
        mesh.material.opacity = THREE.MathUtils.lerp(
          startOpacity,
          endOpacity,
          t
        );
        if (t < 1) requestAnimationFrame(fade);
      }

      requestAnimationFrame(fade);
    }, 1250);
  }

  function restoreElements() {
    fadeOutStartedRef.current = false;

    const fadeDuration = 3000; // 3s fade
    const startTime = performance.now();

    // 1) Force initial opacities to 0 (so we see a real fade-in).
    if (textMaterialRef.current) {
      textMaterialRef.current.opacity = 0;
    }
    if (subTextMeshRef.current?.material) {
      subTextMeshRef.current.material.opacity = 0;
    }
    if (outlineMeshRef.current?.material) {
      outlineMeshRef.current.material.opacity = 0;
    }
    if (logoMeshRef.current?.material) {
      logoMeshRef.current.material.opacity = 0;
    }
    fireflyGroupsRef.current.forEach((group) => {
      if (group.material) {
        group.material.opacity = 0;
      }
    });

    // 2) Make sure relevant objects are visible while the fade is happening.
    if (aboutMeshRef.current) aboutMeshRef.current.visible = true;
    if (aboutHitboxRef.current) aboutHitboxRef.current.visible = true;
    if (contactMeshRef.current) contactMeshRef.current.visible = true;
    if (contactHitboxRef.current) contactHitboxRef.current.visible = true;

    // The "about example" text should stay hidden at the end, so keep it invisible & 0.0
    if (aboutExampleMeshRef.current) {
      aboutExampleMeshRef.current.visible = false;
      aboutExampleMeshRef.current.material.opacity = 0.0;
    }

    // 3) Animate from 0 => final target opacities over 3 seconds.
    function fadeIn() {
      const elapsed = performance.now() - startTime;
      const t = Math.min(elapsed / fadeDuration, 1);

      // Our target final opacities:
      const textTarget = 0.385;
      const subTextTarget = 0.385;
      const outlineTarget = 1.0;
      const logoTarget = 0.5;
      const fireflyTarget = 0.6;

      // Text material  after league she said ggs had fun im going tos eleep
      if (textMaterialRef.current) {
        textMaterialRef.current.opacity = textTarget * t;
      }
      // Subtext
      if (subTextMeshRef.current?.material) {
        subTextMeshRef.current.material.opacity = subTextTarget * t;
      }
      // Outline
      if (outlineMeshRef.current?.material) {
        outlineMeshRef.current.material.opacity = outlineTarget * t;
      }
      // Logo
      if (logoMeshRef.current?.material) {
        logoMeshRef.current.material.opacity = logoTarget * t;
      }
      // Fireflies
      fireflyGroupsRef.current.forEach((group) => {
        if (group.material) {
          group.material.opacity = fireflyTarget * t;
        }
      });

      // Continue the animation until complete
      if (t < 1) {
        requestAnimationFrame(fadeIn);
      }
    }

    requestAnimationFrame(fadeIn);
  }

  function fadeOutElementsForHome() {
    fadeOutStartedRef.current = true;
    const fadeDuration = 1000;
    const startTime = performance.now();

    const initialTextOpacity = textMaterialRef.current?.opacity ?? 0.385;
    const initialSubtextOpacity =
      subTextMeshRef.current?.material?.opacity ?? 0.385;
    const initialOutlineOpacity =
      outlineMeshRef.current?.material?.opacity ?? 1.0;
    const initialLogoOpacity = logoMeshRef.current?.material?.opacity ?? 1.5;
    const initialFireflyOpacity = 0.6;

    function fade() {
      const elapsed = performance.now() - startTime;
      const t = Math.min(elapsed / fadeDuration, 1);

      if (textMaterialRef.current) {
        textMaterialRef.current.opacity = initialTextOpacity * (1 - t);
      }
      if (subTextMeshRef.current?.material) {
        subTextMeshRef.current.material.opacity =
          initialSubtextOpacity * (1 - t);
      }
      if (outlineMeshRef.current?.material) {
        outlineMeshRef.current.material.opacity =
          initialOutlineOpacity * (1 - t);
      }
      if (logoMeshRef.current?.material) {
        logoMeshRef.current.material.opacity = initialLogoOpacity * (1 - t);
      }
      fireflyGroupsRef.current.forEach((group) => {
        if (group.material) {
          group.material.opacity = initialFireflyOpacity * (1 - t);
        }
      });

      if (t < 1) requestAnimationFrame(fade);
    }
    requestAnimationFrame(fade);
  }

  function fadeOutHomeText() {
    const mesh = homeMeshRef.current;
    if (!mesh) return;

    // Animate from current opacity => 0
    const startOpacity = mesh.material.opacity; // e.g. 0.385 or the current value
    const endOpacity = 0;
    const fadeDuration = 1000;
    const startTime = performance.now();

    function fade() {
      const elapsed = performance.now() - startTime;
      const t = Math.min(elapsed / fadeDuration, 1);
      mesh.material.opacity = THREE.MathUtils.lerp(startOpacity, endOpacity, t);

      if (t < 1) requestAnimationFrame(fade);
      else {
        // When done fading, hide + disable
        mesh.visible = false;
        if (homeHitboxRef.current) {
          homeHitboxRef.current.visible = false;
        }
      }
    }

    requestAnimationFrame(fade);
  }

  useEffect(() => {
    let renderer, camera, scene, composer;
    let clock = new THREE.Clock();
    let raycaster, mouseVec2;

    const mount = mountRef.current;
    if (!mount) return;

    const fireflyGroups = [];
    fireflyGroupsRef.current = fireflyGroups;

    function onPointerMove(e) {
      mousePosRef.current.x = e.clientX;
      mousePosRef.current.y = e.clientY;
      if (!raycaster || !mouseVec2) return;

      const { width, height } = mount.getBoundingClientRect();
      mouseVec2.x = (e.clientX / width) * 2 - 1;
      mouseVec2.y = -(e.clientY / height) * 2 + 1;
      raycaster.setFromCamera(mouseVec2, camera);

      // about => LinkedIn
      if (aboutHitboxRef.current) {
        const intersects = raycaster.intersectObject(
          aboutHitboxRef.current,
          false
        );
        setAboutHovered(intersects.length > 0);
      }
      // contact => GitHub
      if (contactHitboxRef.current) {
        const intersects = raycaster.intersectObject(
          contactHitboxRef.current,
          false
        );
        setContactHovered(intersects.length > 0);
      }
      // home
      if (homeHitboxRef.current) {
        const intersects = raycaster.intersectObject(
          homeHitboxRef.current,
          false
        );
        setHomeHovered(intersects.length > 0);
      }
    }

    function onPointerDown() {
      // 1) If clicks are disabled, do nothing
      if (clickDisabledRef.current) {
        console.log("Clicks are currently disabled, ignoring pointerDown");
        return;
      }

      // 2) Immediately disable future clicks for 3s
      clickDisabledRef.current = true;
      setTimeout(() => {
        clickDisabledRef.current = false;
        console.log("Clicks re-enabled after 3s.");
      }, 3000);

      console.log(
        "PointerDown: section=",
        currentSectionRef.current,
        " => about:",
        aboutHoveredRef.current,
        ", contact:",
        contactHoveredRef.current,
        ", home:",
        homeHoveredRef.current
      );

      // HOME -> ABOUT
      if (currentSectionRef.current === "home" && aboutHoveredRef.current) {
        setCurrentSection("about");
        animateCameraAll(
          camera.position.x,
          250,
          camera.position.y,
          0,
          camera.position.z,
          aboutZRef.current + 250,
          camera.rotation.x,
          aboutXRef.current - 1.3,
          camera.rotation.y,
          originalYRef.current - Math.PI / 2 - 0.6,
          camera.rotation.z,
          -1.3,
          2500
        );

        //      if (homeHitboxRef.current)  homeHitboxRef.current.visible = true;
        //    if (aboutMeshRef.current)   aboutMeshRef.current.visible = false;
        //   if (aboutHitboxRef.current) aboutHitboxRef.current.visible = false;
        //   if (contactMeshRef.current) contactMeshRef.current.visible = false;
        //    if (contactHitboxRef.current) contactHitboxRef.current.visible = false;

        //     fadeInHomeText();
        //      fadeOutElementsForHome();
        //     fadeInAboutExampleText();

        //window.open('https://www.linkedin.com/in/jasong7', '_blank', 'noopener,noreferrer');
        // don’t run any of the camera / fade logic

        console.log("home -> about");
        return;
      }

      // HOME -> CONTACT
      if (currentSectionRef.current === "home" && contactHoveredRef.current) {
        setCurrentSection("contact");
        animateCameraAll(
          camera.position.x,
          +150,
          camera.position.y,
          200,
          camera.position.z,
          0,
          camera.rotation.x,
          0,
          camera.rotation.y,
          originalYRef.current - Math.PI - 1.3,
          camera.rotation.z,
          0,
          1000
        );

        if (homeMeshRef.current) homeMeshRef.current.visible = false;
        if (homeHitboxRef.current) homeHitboxRef.current.visible = false;
        if (aboutMeshRef.current) aboutMeshRef.current.visible = false;
        if (aboutHitboxRef.current) aboutHitboxRef.current.visible = false;
        if (contactMeshRef.current) contactMeshRef.current.visible = false;
        if (contactHitboxRef.current) contactHitboxRef.current.visible = false;

        fadeOutElementsForHome();
        return;
      }

      // ABOUT -> HOME
      if (
        currentSectionRef.current === "about" &&
        (homeHoveredRef.current || aboutHoveredRef.current)
      ) {
        setCurrentSection("home");
        animateCameraAll(
          camera.position.x,
          -100,
          camera.position.y,
          20,
          camera.position.z,
          originalZRef.current,
          camera.rotation.x,
          originalXRef.current,
          camera.rotation.y,
          originalYRef.current,
          camera.rotation.z,
          0,
          2500
        );

        // Fade out the HOME text (and disable it)
        fadeOutHomeText();

        // Now we want to *restore* the HOME text if we want it clickable again
        if (homeMeshRef.current) {
          // Make sure it's visible and opacity ~0.385 again
          homeMeshRef.current.visible = true;
          homeMeshRef.current.material.opacity = 0.385;
        }
        if (homeHitboxRef.current) {
          homeHitboxRef.current.visible = true;
        }

        // restoreElements();
        //fadeInHomeText();
        console.log("about -> home");
        return;
      }

      // ABOUT -> CONTACT
      if (currentSectionRef.current === "about" && contactHoveredRef.current) {
        setCurrentSection("contact");
        animateCameraAll();
        //    camera.position.x, 110,
        //  camera.position.y, 110,
        //  camera.position.z, -200,
        // camera.rotation.x, 0,
        //  camera.rotation.y, originalYRef.current - Math.PI,
        //   camera.rotation.z, 0,
        //        1000

        if (aboutMeshRef.current) aboutMeshRef.current.visible = false;
        if (aboutHitboxRef.current) aboutHitboxRef.current.visible = false;
        if (homeMeshRef.current) homeMeshRef.current.visible = false;
        if (homeHitboxRef.current) homeHitboxRef.current.visible = false;
        if (aboutExampleMeshRef.current) {
          aboutExampleMeshRef.current.visible = false;
          aboutExampleMeshRef.current.material.opacity = 0.0;
        }
        return;
      }

      // CONTACT -> HOME
      if (
        currentSectionRef.current === "contact" &&
        (homeHoveredRef.current || contactHoveredRef.current)
      ) {
        setCurrentSection("home");
        animateCameraAll(
          camera.position.x,
          -100,
          camera.position.y,
          20,
          camera.position.z,
          originalZRef.current,
          camera.rotation.x,
          originalXRef.current,
          camera.rotation.y,
          originalYRef.current,
          camera.rotation.z,
          0,
          1000
        );

        restoreElements();
        fadeInHomeText();
        return;
      }

      // CONTACT -> ABOUT
      if (currentSectionRef.current === "contact" && aboutHoveredRef.current) {
        setCurrentSection("about");
        animateCameraAll(
          camera.position.x,
          0,
          camera.position.y,
          10,
          camera.position.z,
          -200,
          camera.rotation.x,
          aboutXRef.current,
          camera.rotation.y,
          originalYRef.current - Math.PI / 2,
          camera.rotation.z,
          0,
          1000
        );

        if (homeMeshRef.current) homeMeshRef.current.visible = false;
        if (homeHitboxRef.current) homeHitboxRef.current.visible = false;
        if (contactMeshRef.current) contactMeshRef.current.visible = false;
        if (contactHitboxRef.current) contactHitboxRef.current.visible = false;

        fadeInAboutExampleText();
        return;
      }
    }

    function init() {
      const { width, height } = mount.getBoundingClientRect();
      scene = new THREE.Scene();

      camera = new THREE.PerspectiveCamera(60, width / height, 0.1, 200000);
      camera.position.set(-100, 20, originalZRef.current);
      camera.rotation.x = originalXRef.current;
      camera.rotation.y = originalYRef.current;
      cameraRef.current = camera;
      scene.add(camera);

      raycaster = new THREE.Raycaster();
      mouseVec2 = new THREE.Vector2();

      const pointLight = new THREE.PointLight(0xffffff, 1, 1000);
      pointLight.position.set(0, 20, 0);
      scene.add(pointLight);

      renderer = new THREE.WebGLRenderer({ antialias: true });
      renderer.setSize(width, height);
      renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
      renderer.shadowMap.type = THREE.PCFSoftShadowMap;
      renderer.toneMapping = THREE.ACESFilmicToneMapping;
      renderer.toneMappingExposure = 0.8;
      mount.appendChild(renderer.domElement);

      const gltfLoader = new GLTFLoader();

      // 1) Load portal
      gltfLoader.load(
        "/portal/portal2/scene8.gltf",
        (gltf) => {
          const model = gltf.scene;
          model.traverse((node) => {
            if (node.isMesh) {
              node.castShadow = true;
              node.receiveShadow = true;
            }
          });
          // If portal has animations
          if (gltf.animations?.length > 0) {
            mixerPortal = new THREE.AnimationMixer(model);
            gltf.animations.forEach((clip) => {
              const action = mixerPortal.clipAction(clip);
              // Slow the animation to half speed
              action.setEffectiveTimeScale(0.5);
              action.play();
            });
          }
          scene.add(model);
        },
        undefined,
        (error) => console.error("Error loading portal scene:", error)
      );

      // 2) Load blue dragon
      gltfLoader.load(
        "blue_dragon/scene.gltf",
        (gltf) => {
          console.log("Blue Dragon loaded:", gltf);
          const dragon = gltf.scene;

          dragon.scale.set(5000, 5000, 5000);
          dragon.position.set(-160, -5, -120);
          dragon.rotation.set(0, 3.8, 0);

          // If the dragon has animations
          if (gltf.animations && gltf.animations.length > 5) {
            mixerDragon = new THREE.AnimationMixer(dragon);
            // Play one of the animations
            const clip = gltf.animations[4];
            const action = mixerDragon.clipAction(clip);
            action.play();
            // Slow it down to half speed
            action.setEffectiveTimeScale(0.5);
          }

          // Optional bounding box for debugging
          const bbox = new THREE.Box3().setFromObject(dragon);
          console.log("Dragon bounding box:", bbox);

          scene.add(dragon);
          console.log("Dragon added to scene.");

          // Store reference so we can rotate it later
          dragonRef.current = dragon;
        },
        undefined,
        (err) => console.error("Error loading blue_dragon:", err)
      );

      // Setup text + logo
      setupLogoAndText({
        camera,
        scene,
        glowMaterialRef,
        textMaterialRef,
        subTextMeshRef,
        aboutMeshRef,
        aboutHitboxRef,
        contactMeshRef,
        contactHitboxRef,
        homeMeshRef,
        homeHitboxRef,
        outlineMeshRef,
        logoMeshRef,
        aboutExampleMeshRef,
      });

      // Fireflies
      const textures = ["/particles.png", "/particleswhite.png"];
      textures.forEach((url) => {
        const group = createFireflyGroup(url, 20);
        camera.add(group.points);

        group.points.position.z = -30;
        fireflyGroups.push(group);
      });

      composer = new EffectComposer(renderer);
      const renderPass = new RenderPass(scene, camera);
      composer.addPass(renderPass);

      const bloomPass = new UnrealBloomPass(
        new THREE.Vector2(width, height),
        0.9,
        0.3,
        0.08
      );
      composer.addPass(bloomPass);

      window.addEventListener("resize", onWindowResize);

      function animate() {
        requestAnimationFrame(animate);
        const delta = clock.getDelta();

        // Update mixers
        if (mixerPortal) mixerPortal.update(delta);
        if (mixerDragon) mixerDragon.update(delta);

        globalTimeRef.current += delta;
        animateIconAndTextGlow();

        // Rotate dragon based on mouse
        if (dragonRef.current) {
          // Convert mouse coords to [-1..1] for left-right rotation
          const { width, height } = mount.getBoundingClientRect();
          const normX = (mousePosRef.current.x / width) * 2 - 1;
          dragonRef.current.rotation.y = 3.8 + 0.5 * normX; // adjust 0.5 as desired
        }

        // Fireflies
        const { width, height } = mount.getBoundingClientRect();
        const [mx, my] = screenToFireflySpace(
          mousePosRef.current.x,
          mousePosRef.current.y,
          width,
          height,
          25
        );
        fireflyGroupsRef.current.forEach((group) =>
          updateFireflies(group, delta, mx, my)
        );

        composer.render();
      }
      animate();
    }

    function animateIconAndTextGlow() {
      const t = globalTimeRef.current;
      const pulse = 1.0 + 0.2 * Math.sin(t * 2);
      if (glowMaterialRef.current) {
        glowMaterialRef.current.emissiveIntensity = pulse;
      }
      if (!fadeOutStartedRef.current && textMaterialRef.current) {
        const baseOpacity = 0.385;
        textMaterialRef.current.opacity = baseOpacity * pulse;
      }
    }

    function onWindowResize() {
      if (!mount) return;
      const { width, height } = mount.getBoundingClientRect();
      renderer.setSize(width, height);
      renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
      camera.aspect = width / height;
      camera.updateProjectionMatrix();
      composer.setSize(width, height);
    }

    init();

    window.addEventListener("pointermove", onPointerMove);
    window.addEventListener("pointerdown", onPointerDown);

    return () => {
      window.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("pointerdown", onPointerDown);
      window.removeEventListener("resize", onWindowResize);
      if (renderer) renderer.dispose();
      if (mount && mount.firstChild) mount.removeChild(mount.firstChild);
    };
  }, []);

  // Hover effects
  useEffect(() => {
    if (aboutMeshRef.current) {
      aboutMeshRef.current.material.opacity = aboutHovered ? 1.0 : 0.385;
    }
  }, [aboutHovered]);

  useEffect(() => {
    if (contactMeshRef.current) {
      contactMeshRef.current.material.opacity = contactHovered ? 1.0 : 0.385;
    }
  }, [contactHovered]);

  useEffect(() => {
    if (homeMeshRef.current) {
      homeMeshRef.current.material.opacity = homeHovered ? 1.0 : 0.385;
    }
  }, [homeHovered]);

  const [canClick, setCanClick] = useState(true);
  const withThrottle = (fn) => () => {
    if (!canClick) return;
    setCanClick(false);
    fn();
    setTimeout(() => setCanClick(true), 3000);
  };

  return (
    <div style={{ width: "100vw", height: "100vh", position: "relative" }}>
      {/* Three.js Canvas */}
      <div ref={mountRef} style={{ width: "100%", height: "100%" }} />
      {currentSection === "about" &&
        (window.innerWidth < 768 ? (
          // MOBILE VIEW: Just show Hello World
          <div
            style={{
              pointerEvents: "auto",
              position: "absolute",
              top: "20%",
              left: "50%",
              transform: "translateX(-50%)",
              width: "90%",
              textAlign: "center",
              color: "#fff",
              fontFamily: "'Segoe UI', sans-serif",
              opacity: aboutOverlayVisible ? 1 : 0,
              transition: "opacity 0.8s ease-in-out",
            }}
          >
               <div
              style={{
                flex: "1 1 500px",
                //paddingRight: "2rem",
                color: "#fff",
                fontFamily: "Inter, sans-serif",
                position: "relative",
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
               //   backgroundImage: "url('logo3.png')",
                  backgroundSize: "contain",
                  backgroundRepeat: "no-repeat",
                  backgroundPosition: "center",
                  opacity: 0.5,
                  zIndex: 1,
                  pointerEvents: "none",
                }}
              />
              <div style={{ position: "relative", zIndex: 1 }}>
                <h1
                  style={{
                    fontSize: "1.7rem",
                    fontWeight: "900",
                    lineHeight: 1.2,
                  margin: "1.5rem 0",
                    textShadow: "0 0 10px rgba(255,255,255,0.2)",
                  }}
                >
                  <i>JASONGODFREY.DEV</i>
                  <br />
                  <span style={{ fontWeight: 800 }}>SOFTWARE ENGINEERING</span>
                </h1>

                <p
                  style={{
                    fontSize: "1.25rem",
                    lineHeight: 1.8,
                    marginBottom: "2rem",
                    maxWidth: "500px",
                    opacity: 0.85,
                    textShadow: "0 0 6px rgb(0, 0, 0)",
                  }}
                >
                  <b>Full-Stack Web and Game Development:</b>
                   <video
src="https://res.cloudinary.com/dzseffxgd/video/upload/Untitled_video_-_Made_with_Clipchamp_e7vdfv.mp4" autoPlay
  loop
  muted
  playsInline
  preload="metadata"
  onLoadedMetadata={e => { e.currentTarget.currentTime = 0 }}
  style={{
    position: "absolute",
    top: 100,
    left: "0%",
    width: "100%",
    height: "80%",
    objectFit: "cover",
    borderRadius: "14px",
    zIndex: -1,
    //backgroundColor: "rgba(0, 128, 0, 1.0)",
  }}
/>
                  <br />
                  Specialized in React and building 3D interactive web
                  experiences.
                </p>
{/*}
                <div style={{ display: "flex", gap: "1rem" }}>
                  <a
                    href="https://linkedin.com/in/jasong7"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ textDecoration: "none" }}
                  >
                    <button
                      style={{
                        backgroundColor: "#0cf",
                        color: "#000",
                        fontWeight: "bold",
                        padding: "0.75rem 1.5rem",
                        border: "none",
                        borderRadius: "0.5rem",
                        cursor: "pointer",
                        transition:
                          "background-color 0.3s ease, transform 0.2s ease",
                      }}
                      onMouseOver={(e) => {
                        e.currentTarget.style.backgroundColor =
                          "rgba(87, 246, 252, 0.99)";
                      }}
                      onMouseOut={(e) => {
                        e.currentTarget.style.backgroundColor = "#0cf";
                      }}
                    >
                      Contact
                    </button>
                  </a>

                  <button
                    style={{
                      backgroundColor: "transparent",
                      color: "#fff",
                      border: "2px solid #0cf",
                      padding: "0.75rem 1.5rem",
                      borderRadius: "0.5rem",
                      cursor: "pointer",
                      transition: "all 0.3s ease",
                    }}
                    onMouseOver={(e) => {
                      e.currentTarget.style.backgroundColor =
                        "rgba(9, 206, 255, 0.44)";
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.backgroundColor = "transparent";
                      e.currentTarget.style.color = "#fff";
                    }}
                    onMouseDownCapture={(e) => {
                      e.currentTarget.textContent = "Coming Soon";
                    }}
                  >
                    My Work
                  </button>   
                </div>*/}
              </div>
            </div>

          </div>
        ) : (
          // DESKTOP VIEW: Keep full original layout
          <div
            style={{
              pointerEvents: "auto",
              position: "absolute",
              top: "15%",
              left: "50%",
              transform: "translateX(-50%)",
              scale: "0.8",
              width: "100rem",
              zIndex: 1000,
              display: "flex",
              flexDirection: "row",
              flexWrap: "wrap",
              alignItems: "center",
              justifyContent: "space-between",
              color: "#fff",
              padding: "3rem 2rem",
              fontFamily: "'Segoe UI', sans-serif",
              opacity: aboutOverlayVisible ? 1 : 0,
              transition: "opacity 0.8s ease-in-out",
            }}
          >
            {/* LEFT COLUMN */}
            <div
              style={{
                flex: "1 1 500px",
                paddingRight: "2rem",
                color: "#fff",
                fontFamily: "Inter, sans-serif",
                position: "relative",
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundImage: "url('logo3.png')",
                  backgroundSize: "contain",
                  backgroundRepeat: "no-repeat",
                  backgroundPosition: "center",
                  opacity: 0.5,
                  zIndex: 1,
                  pointerEvents: "none",
                }}
              />
              <div style={{ position: "relative", zIndex: 1 }}>
                <h1
                  style={{
                    fontSize: "3rem",
                    fontWeight: "900",
                    lineHeight: 1.2,
                    margin: "1rem 0",
                    textShadow: "0 0 10px rgba(255,255,255,0.2)",
                  }}
                >
                  <i>JASONGODFREY.DEV</i>
                  <br />
                  <span style={{ fontWeight: 800 }}>SOFTWARE ENGINEERING</span>
                </h1>

                <p
                  style={{
                    fontSize: "1.25rem",
                    lineHeight: 1.8,
                    marginBottom: "2rem",
                    maxWidth: "500px",
                    opacity: 0.85,
                  }}
                >
                  <b>Full-Stack Web and Game Development:</b>
                  <br />
                  Specialized in React and building 3D interactive web
                  experiences. ⊂(◉‿◉)つ
                </p>

                <div style={{ display: "flex", gap: "1rem" }}>
                  <a
                    href="https://linkedin.com/in/jasong7"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ textDecoration: "none" }}
                  >
                    <button
                      style={{
                        backgroundColor: "#0cf",
                        color: "#000",
                        fontWeight: "bold",
                        padding: "0.75rem 1.5rem",
                        border: "none",
                        borderRadius: "0.5rem",
                        cursor: "pointer",
                        transition:
                          "background-color 0.3s ease, transform 0.2s ease",
                      }}
                      onMouseOver={(e) => {
                        e.currentTarget.style.backgroundColor =
                          "rgba(87, 246, 252, 0.99)";
                      }}
                      onMouseOut={(e) => {
                        e.currentTarget.style.backgroundColor = "#0cf";
                      }}
                    >
                      Contact
                    </button>
                  </a>

                  <button
                    style={{
                      backgroundColor: "transparent",
                      color: "#fff",
                      border: "2px solid #0cf",
                      padding: "0.75rem 1.5rem",
                      borderRadius: "0.5rem",
                      cursor: "pointer",
                      transition: "all 0.3s ease",
                    }}
                    onMouseOver={(e) => {
                      e.currentTarget.style.backgroundColor =
                        "rgba(9, 206, 255, 0.44)";
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.backgroundColor = "transparent";
                      e.currentTarget.style.color = "#fff";
                    }}
                    onMouseDownCapture={(e) => {
                      e.currentTarget.textContent = "Coming Soon";
                    }}
                  >
                    My Work
                  </button>
                </div>
              </div>
            </div>

            {/* RIGHT COLUMN */}
            <div
              style={{
                flex: "0 0 800px",
                borderRadius: "14px",
                minHeight: "800px",
                overflow: "hidden",
              }}
            >
             <video
 src="https://res.cloudinary.com/dzseffxgd/video/upload/Untitled_video_-_Made_with_Clipchamp_e7vdfv.mp4" autoPlay
  loop
  muted
  playsInline
  preload="metadata"
  onLoadedMetadata={e => { e.currentTarget.currentTime = 0 }}
  style={{
    position: "absolute",
    top: 0,
    left: "24%",
    width: "75%",
    height: "80%",
    objectFit: "cover",
    borderRadius: "14px",
    zIndex: 0,
    //backgroundColor: "rgba(0, 128, 0, 1.0)",
  }}
/>

              <div
                style={{
                  position: "relative",
                  zIndex: 1,
                  width: "100%",
                  height: "100%",
                  backgroundColor: "rgba(0, 128, 0, 0.2)",
                  borderRadius: "14px",
                }}
              />
            </div>
          </div>



        ))}
      /* Contact overlay text */
      {currentSection === "contact" && (
        <div
          style={{
            pointerEvents: currentSection === "contact" ? "auto" : "none",
            position: "absolute",
            top: "30%",
            left: 0,
            width: "100%",
            height: "40%",
            backgroundColor: "rgba(0,0,0,0.5)",
            zIndex: 10,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: "1.5rem",
            color: "#fff",
            fontSize: "3vh",
            opacity: currentSection === "contact" ? 1 : 0,
            transition: "opacity 3s ease",
          }}
        >
          <p style={{ margin: 0, textAlign: "center" }}></p>

          <div style={{ display: "flex", gap: "2rem" }}>
            <a
              href="https://github.com/jasonygodfrey"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color: "inherit",
                textDecoration: "none",
                transition: "filter 0.3s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.filter = "drop-shadow(0 0 10px cyan)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.filter = "none";
              }}
            >
              <FaGithub style={{ fontSize: "clamp(40px, 8vw, 160px)" }} />
            </a>

            <a
              href="mailto:jasonygodfrey@gmail.com"
              style={{
                color: "inherit",
                textDecoration: "none",
                transition: "filter 0.3s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.filter = "drop-shadow(0 0 10px cyan)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.filter = "none";
              }}
            >
              <FaEnvelope style={{ fontSize: "clamp(40px, 8vw, 160px)" }} />
            </a>

            <a
              href="https://www.linkedin.com/in/jasong7"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color: "inherit",
                textDecoration: "none",
                transition: "filter 0.3s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.filter = "drop-shadow(0 0 10px cyan)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.filter = "none";
              }}
            >
              <FaLinkedin style={{ fontSize: "clamp(40px, 8vw, 160px)" }} />
            </a>
          </div>

          <p style={{ margin: 0, textAlign: "center" }}></p>
        </div>
      )}
      {/* Navigation buttons */}
      <div
        style={{
          position: "absolute",
          top: "75%",
          left: "50%",
          transform: "translateX(-50%)",
          zIndex: 20,
          display: "flex",
          gap: "20px",
        }}
      >
        {/* HOME Button */}
        <button
          onClick={withThrottle(() => {
            if (currentSectionRef.current !== "home") {
              if (window._flyAroundStore?.id) {
                cancelAnimationFrame(window._flyAroundStore.id);
              }
              setCurrentSection("home");
              const cam = cameraRef.current;
              if (!cam) return;
              animateCameraAll(
                cam.position.x,
                -100,
                cam.position.y,
                20,
                cam.position.z,
                originalZRef.current,
                cam.rotation.x,
                originalXRef.current,
                cam.rotation.y,
                originalYRef.current,
                cam.rotation.z,
                0,
                2500
              );
              restoreElements();
              fadeInHomeText();
            }
          })}
          style={{
            padding: "10px 20px",
            fontSize: "clamp(1rem, 2vw, 2rem)",
            fontFamily: "Coelacanth, serif",
            background: "transparent",
            border: "none",
            cursor: "pointer",
            color:
              currentSection === "home" ? "white" : "rgba(255,255,255,0.6)",
            borderBottom:
              currentSection === "home"
                ? "2px solid white"
                : "2px solid transparent",
            transition: "all 0.3s ease",
            textShadow: "0 0 5px rgba(255,255,255,0)",
          }}
          onMouseOver={(e) => {
            e.target.style.textShadow = "0 0 10px rgba(255,255,255,0.6)";
            if (currentSection !== "home") {
              e.target.style.borderBottom = "2px solid rgba(255,255,255,0.3)";
            }
          }}
          onMouseOut={(e) => {
            e.target.style.textShadow = "0 0 5px rgba(255,255,255,0)";
            if (currentSection !== "home") {
              e.target.style.borderBottom = "2px solid transparent";
            }
          }}
        >
          HOME
        </button>

        {/* ABOUT Button */}
        <button
          onClick={withThrottle(() => {
            if (currentSectionRef.current !== "about") {
              if (window._flyAroundStore?.id) {
                cancelAnimationFrame(window._flyAroundStore.id);
              }
              setCurrentSection("about");
              const cam = cameraRef.current;
              if (!cam) return;
              animateCameraAll(
                cam.position.x,
                250,
                cam.position.y,
                0,
                cam.position.z,
                aboutZRef.current + 250,
                cam.rotation.x,
                aboutXRef.current - 1.3,
                cam.rotation.y,
                originalYRef.current - Math.PI / 2 - 0.6,
                cam.rotation.z,
                -1.3,
                2500
              );
              fadeOutElementsForHome();
              fadeInAboutExampleText();
            }
          })}
          style={{
            padding: "10px 20px",
            fontSize: "clamp(1rem, 2vw, 2rem)",
            fontFamily: "Coelacanth, serif",
            background: "transparent",
            border: "none",
            cursor: "pointer",
            color:
              currentSection === "about" ? "white" : "rgba(255,255,255,0.6)",
            borderBottom:
              currentSection === "about"
                ? "2px solid white"
                : "2px solid transparent",
            transition: "all 0.3s ease",
            textShadow: "0 0 5px rgba(255,255,255,0)",
          }}
          onMouseOver={(e) => {
            e.target.style.textShadow = "0 0 10px rgba(255,255,255,0.6)";
            if (currentSection !== "about") {
              e.target.style.borderBottom = "2px solid rgba(255,255,255,0.3)";
            }
          }}
          onMouseOut={(e) => {
            e.target.style.textShadow = "0 0 5px rgba(255,255,255,0)";
            if (currentSection !== "about") {
              e.target.style.borderBottom = "2px solid transparent";
            }
          }}
        >
          ABOUT
        </button>

        {/* CONTACT Button */}
        <button
          onClick={withThrottle(() => {
            const cam = cameraRef.current;
            if (!cam) return;
            if (!window._flyAroundStore) window._flyAroundStore = {};
            const flyAroundStore = window._flyAroundStore;
            if (flyAroundStore.id) cancelAnimationFrame(flyAroundStore.id);

            if (currentSectionRef.current !== "contact") {
              setCurrentSection("contact");
              fadeInContactExampleText();

              const from = {
                x: cam.position.x,
                y: cam.position.y,
                z: cam.position.z,
                rx: cam.rotation.x,
                ry: cam.rotation.y,
                rz: cam.rotation.z,
              };
              const to = { x: -420, y: 20, z: -70, rx: 0, ry: -1.7, rz: 0 };
              const duration = 1800;
              const start = performance.now();

              function easeInOut(t) {
                return t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
              }

              function animateToPosition(time) {
                const t = Math.min((time - start) / duration, 1);
                const eased = easeInOut(t);

                cam.position.x = from.x + (to.x - from.x) * eased;
                cam.position.y = from.y + (to.y - from.y) * eased;
                cam.position.z = from.z + (to.z - from.z) * eased;
                cam.rotation.x = from.rx + (to.rx - from.rx) * eased;
                cam.rotation.y = from.ry + (to.ry - from.ry) * eased;
                cam.rotation.z = from.rz + (to.rz - from.rz) * eased;

                if (t < 1) {
                  requestAnimationFrame(animateToPosition);
                } else {
                  startFlyAround(cam.position.x, cam.position.z);
                }
              }

              function startFlyAround(initialX, initialZ) {
                const radius = Math.sqrt(initialX ** 2 + initialZ ** 2);
                let angle = Math.atan2(initialZ, initialX);

                function fly() {
                  angle += 0.002;
                  cam.position.x = Math.cos(angle) * radius;
                  cam.position.z = Math.sin(angle) * radius;
                  cam.lookAt(0, 0, 0);
                  flyAroundStore.id = requestAnimationFrame(fly);
                }
                fly();
              }

              requestAnimationFrame(animateToPosition);
              fadeOutElementsForHome();
            } else {
              if (flyAroundStore.id) {
                cancelAnimationFrame(flyAroundStore.id);
                flyAroundStore.id = null;
              }
            }
          })}
          style={{
            padding: "10px 20px",
            fontSize: "clamp(1rem, 2vw, 2rem)",
            fontFamily: "Coelacanth, serif",
            background: "transparent",
            border: "none",
            cursor: "pointer",
            color:
              currentSection === "contact" ? "white" : "rgba(255,255,255,0.6)",
            borderBottom:
              currentSection === "contact"
                ? "2px solid white"
                : "2px solid transparent",
            transition: "all 0.3s ease",
            textShadow: "0 0 5px rgba(255,255,255,0)",
          }}
          onMouseOver={(e) => {
            e.target.style.textShadow = "0 0 10px rgba(255,255,255,0.6)";
            if (currentSection !== "contact") {
              e.target.style.borderBottom = "2px solid rgba(255,255,255,0.3)";
            }
          }}
          onMouseOut={(e) => {
            e.target.style.textShadow = "0 0 5px rgba(255,255,255,0)";
            if (currentSection !== "contact") {
              e.target.style.borderBottom = "2px solid transparent";
            }
          }}
        >
          CONTACT
        </button>
      </div>
      {/* Gradient overlay */}
      <div
        style={{
          pointerEvents: "none",
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          background:
            "radial-gradient(circle at 60% 65%, rgba(255,255,255,0.0) 0%, rgba(0,0,0,0.25) 100%)",
        }}
      />
    </div>
  );
}

export default App;
